// Inbound attribution: capture utm_source/utm_medium/utm_campaign (and the
// shorter ?ref= alias) from the current URL, persist for the session so any
// downstream event (vote, signup, share) can tag itself with the original
// source. Stored in sessionStorage — not localStorage — so it resets per
// browsing session, which matches how campaign attribution should behave.
//
// On first capture per session, also fire-and-forget a tracking POST to
// the backend so an InboundClick row is written. This lets the admin
// dashboard report share→click attribution even when the visitor never
// re-shares (the re-share path writes its own inbound info on ShareEvent).
import { DOMAIN } from '../redux/constants';

const KEY = 'pickpls.attribution';
const REPORTED_KEY = 'pickpls.attribution.reported';

function reportInbound(attr) {
  // Don't double-report the same inbound within a session. If the user
  // clicks a share link, lands, then reloads or navigates, we only want
  // one server-side row per session.
  try {
    if (sessionStorage.getItem(REPORTED_KEY) === attr.source) return;
    sessionStorage.setItem(REPORTED_KEY, attr.source);
  } catch { /* sessionStorage unavailable; still fire the beacon */ }

  const body = JSON.stringify({
    source: attr.source,
    medium: attr.medium,
    campaign: attr.campaign,
    path: attr.landingPath,
  });
  // sendBeacon survives page-nav races better than fetch, but not all
  // browsers. Fall back to fetch with keepalive. Both fire-and-forget.
  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(
        `${DOMAIN}/api/v1/engagement/inbound/`,
        new Blob([body], { type: 'application/json' }),
      );
      return;
    }
  } catch { /* fall through to fetch */ }
  try {
    fetch(`${DOMAIN}/api/v1/engagement/inbound/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch { /* ignore — tracking must never block the page */ }
}

export function captureAttribution() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source') || params.get('ref');
    if (!utmSource) return readAttribution();

    const attr = {
      source: utmSource.toLowerCase().slice(0, 40),
      medium: (params.get('utm_medium') || 'unknown').slice(0, 40),
      campaign: (params.get('utm_campaign') || 'unknown').slice(0, 40),
      landingPath: window.location.pathname,
      capturedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(KEY, JSON.stringify(attr));
    reportInbound(attr);
    return attr;
  } catch {
    return null;
  }
}

export function readAttribution() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearAttribution() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(KEY);
    sessionStorage.removeItem(REPORTED_KEY);
  } catch {
    /* ignore */
  }
}
